import { Input } from "antd";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  addToPersonalDictionary,
  ensureSpellChecker,
  findMisspelled,
  ignoreWord,
  isKnownCorrect,
  onDictionaryChange,
  suggestFor,
} from "./spellClient";
import {
  COMMON_TYPOS,
  isIgnorable,
  matchCase,
  normalizeWord,
  pickAutoCorrection,
  tokenize,
} from "./spellUtils";

const MIRRORED_STYLES = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "textTransform",
  "textIndent",
  "tabSize",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
];

const DELIMITER = /^[\s.,!?;:)]$/;

/** Replaces [start, end) through the browser so Ctrl+Z can undo it. */
function replaceRange(field, start, end, replacement) {
  field.focus();
  field.setSelectionRange(start, end);
  let done = false;
  try {
    done = document.execCommand("insertText", false, replacement);
  } catch (e) {
    done = false;
  }
  if (!done) {
    // Fallback for browsers without execCommand on form fields: write the
    // value natively and let React see an input event.
    const proto =
      field.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value").set;
    const next =
      field.value.slice(0, start) + replacement + field.value.slice(end);
    setter.call(field, next);
    const caret = start + replacement.length;
    field.setSelectionRange(caret, caret);
    field.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

function SuggestionMenu({ menu, onPick, onIgnore, onAdd, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: menu.x, top: menu.y });

  // Place the menu once its real size is known: below the word when it fits,
  // otherwise above it, always clamped inside the viewport.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const margin = 8;
    const { width, height } = el.getBoundingClientRect();
    const below = window.innerHeight - menu.anchorBottom - margin;
    const above = menu.anchorTop - margin;
    let top;
    if (height <= below) top = menu.anchorBottom + 4;
    else if (height <= above) top = menu.anchorTop - height - 4;
    else top = below >= above ? menu.anchorBottom + 4 : margin;
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));
    const left = Math.max(
      margin,
      Math.min(menu.x, window.innerWidth - width - margin),
    );
    setPos({ left, top });
  }, [menu.anchorBottom, menu.anchorTop, menu.x, menu.suggestions]);

  useEffect(() => {
    const close = () => onClose();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("mousedown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const item = {
    padding: "5px 14px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
  const hover = (e, on) => {
    e.currentTarget.style.background = on ? "#f0f5ff" : "transparent";
  };
  const entry = (label, handler, style) => (
    <div
      key={label}
      role="menuitem"
      style={{ ...item, ...style }}
      onMouseEnter={(e) => hover(e, true)}
      onMouseLeave={(e) => hover(e, false)}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handler();
      }}
    >
      {label}
    </div>
  );

  return ReactDOM.createPortal(
    <div
      ref={ref}
      role="menu"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        // Above any app chrome (headers, bottom bars, modals).
        zIndex: 2147483000,
        minWidth: 170,
        maxHeight: "calc(100vh - 16px)",
        overflowY: "auto",
        padding: "4px 0",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
        fontSize: 14,
      }}
    >
      {menu.suggestions === null ? (
        <div style={{ ...item, color: "#9ca3af" }}>Checking…</div>
      ) : menu.suggestions.length ? (
        menu.suggestions
          .slice(0, 5)
          .map((s) => entry(s, () => onPick(s), { fontWeight: 600 }))
      ) : (
        <div style={{ ...item, color: "#9ca3af" }}>No suggestions</div>
      )}
      <div style={{ borderTop: "1px solid #eee", margin: "4px 0" }} />
      {entry("Ignore", onIgnore)}
      {entry("Add to dictionary", onAdd)}
    </div>,
    document.body,
  );
}

/**
 * Wraps an antd Input / Input.TextArea with a Word-style spell checker:
 * a red wavy underline on misspelled words, right-click suggestions, and
 * automatic fixes for obvious typos when a word is finished.
 * Messages stay plain text; only the editing experience changes.
 */
const SpellChecked = forwardRef(function SpellChecked(
  {
    as,
    value,
    onChange,
    onFocus,
    onScroll,
    onSelect,
    onContextMenu,
    onKeyDown,
    ...rest
  },
  forwardedRef,
) {
  const Component = as === "input" ? Input : Input.TextArea;
  const instRef = useRef(null);
  const backdropRef = useRef(null);
  const caretRef = useRef(-1);
  // The screens' own handlers (e.g. Enter = send) read their latest state, so
  // replay through a ref instead of a stale closure.
  const latest = useRef({});
  latest.current = { onKeyDown };

  const [status, setStatus] = useState("idle"); // idle | ready | error
  const [innerText, setInnerText] = useState("");
  const [bad, setBad] = useState([]);
  const [dictVersion, setDictVersion] = useState(0);
  const [menu, setMenu] = useState(null);

  const text = typeof value === "string" ? value : innerText;

  const setRefs = useCallback(
    (node) => {
      instRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const getField = useCallback(() => {
    const inst = instRef.current;
    if (!inst) return null;
    return as === "input"
      ? inst.input || null
      : inst.resizableTextArea?.textArea || null;
  }, [as]);

  // Keep the underline layer exactly over the field.
  const syncBackdrop = useCallback(() => {
    const field = getField();
    const layer = backdropRef.current;
    if (!field || !layer) return;
    const cs = window.getComputedStyle(field);
    const fr = field.getBoundingClientRect();
    const bl = parseFloat(cs.borderLeftWidth) || 0;
    const br = parseFloat(cs.borderRightWidth) || 0;
    // The layer is a sibling of the field (so existing layouts are untouched);
    // position it in its own containing block.
    const op = layer.offsetParent;
    if (op && op !== document.body && op !== document.documentElement) {
      const or = op.getBoundingClientRect();
      layer.style.top = `${fr.top - or.top - op.clientTop + op.scrollTop}px`;
      layer.style.left = `${fr.left - or.left - op.clientLeft + op.scrollLeft}px`;
    } else {
      layer.style.top = `${fr.top + window.scrollY}px`;
      layer.style.left = `${fr.left + window.scrollX}px`;
    }
    layer.style.width = `${field.clientWidth + bl + br}px`;
    layer.style.height = `${fr.height}px`;
    MIRRORED_STYLES.forEach((p) => {
      layer.style[p] = cs[p];
    });
    layer.scrollTop = field.scrollTop;
    layer.scrollLeft = field.scrollLeft;
  }, [getField]);

  useLayoutEffect(() => {
    syncBackdrop();
  });

  useEffect(() => {
    const field = getField();
    if (!field) return undefined;
    const sync = () => syncBackdrop();
    field.addEventListener("scroll", sync);
    window.addEventListener("resize", sync);
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(sync);
      observer.observe(field);
    }
    return () => {
      field.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
      if (observer) observer.disconnect();
    };
  }, [getField, syncBackdrop, status]);

  useEffect(() => onDictionaryChange(() => setDictVersion((v) => v + 1)), []);

  const handleFocus = (e) => {
    if (onFocus) onFocus(e);
    if (status === "idle") {
      ensureSpellChecker().then(
        () => setStatus("ready"),
        () => setStatus("error"),
      );
    }
  };

  // Re-check (debounced) whenever the text or the user's dictionary changes.
  useEffect(() => {
    if (status !== "ready") return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const tokens = tokenize(text).filter((t) => !isIgnorable(text, t));
      const words = [...new Set(tokens.map((t) => normalizeWord(t.word)))];
      try {
        const wrong = await findMisspelled(words);
        if (cancelled) return;
        setBad(tokens.filter((t) => wrong.has(normalizeWord(t.word))));
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text, status, dictVersion]);

  // Word that ends exactly at `pos` in `text_`, with its range.
  const wordBefore = (text_, pos) => {
    let start = pos;
    while (start > 0 && /[A-Za-zÀ-ɏ'’]/.test(text_[start - 1])) {
      start--;
    }
    const word = text_.slice(start, pos);
    return word ? { word, start, end: pos } : null;
  };

  /** Replacement for a just-finished word, or null to leave it alone. */
  const computeReplacement = async (text_, token) => {
    if (isIgnorable(text_, token)) return null;
    const { word } = token;
    const lower = normalizeWord(word);
    if (COMMON_TYPOS[lower]) return matchCase(word, COMMON_TYPOS[lower]);
    if (word !== lower && !/^[A-Z][a-z]+$/.test(word)) return null;
    if (isKnownCorrect(lower)) return null;
    const wrong = await findMisspelled([lower]);
    if (!wrong.has(lower)) return null;
    const best = pickAutoCorrection(word, await suggestFor(word));
    return best ? matchCase(word, best) : null;
  };

  const maybeAutocorrect = (field) => {
    const pos = field.selectionStart;
    const snapshot = field.value;
    const token = wordBefore(snapshot, pos - 1); // word before the delimiter
    if (!token) return;
    computeReplacement(snapshot, token)
      .then((fixed) => {
        // Abort if the user kept typing or moved the caret meanwhile.
        if (!fixed || field.value !== snapshot || field.selectionStart !== pos) {
          return;
        }
        replaceRange(field, token.start, token.end, fixed);
        const caret = pos + (fixed.length - token.word.length);
        field.setSelectionRange(caret, caret);
      })
      .catch(() => {});
  };

  // Enter finishes the last word too. Screens that send on Enter call
  // preventDefault in their own handler, so fix the word first and then hand
  // the key to the screen once the corrected text is in its state.
  const handleKeyDown = (e) => {
    const field = e.target;
    const plainEnter =
      e.key === "Enter" &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      !(e.nativeEvent && e.nativeEvent.isComposing);
    if (!plainEnter || status !== "ready" || !field || !field.value) {
      if (onKeyDown) onKeyDown(e);
      return;
    }
    const pos = field.selectionStart;
    const snapshot = field.value;
    const token = pos === field.selectionEnd ? wordBefore(snapshot, pos) : null;
    const lower = token && normalizeWord(token.word);
    const candidate =
      token &&
      !isIgnorable(snapshot, token) &&
      !(lower && isKnownCorrect(lower));
    if (!candidate) {
      if (onKeyDown) onKeyDown(e);
      return;
    }

    e.preventDefault();
    const synthetic = {
      key: "Enter",
      shiftKey: false,
      ctrlKey: false,
      metaKey: false,
      altKey: false,
      target: field,
      currentTarget: field,
      nativeEvent: e.nativeEvent,
      defaultPrevented: false,
      preventDefault() {
        this.defaultPrevented = true;
      },
      stopPropagation() {},
      persist() {},
    };
    const finish = () => {
      const handler = latest.current.onKeyDown;
      if (handler) handler(synthetic);
      // Nobody claimed Enter: it was meant to add a line break.
      if (!synthetic.defaultPrevented && as !== "input") {
        const at = field.selectionStart;
        replaceRange(field, at, at, "\n");
      }
    };
    computeReplacement(snapshot, token)
      .then((fixed) => {
        if (fixed && field.value === snapshot && field.selectionStart === pos) {
          replaceRange(field, token.start, token.end, fixed);
        }
      })
      .catch(() => {})
      .then(() => setTimeout(finish, 0));
  };

  const handleChange = (e) => {
    const field = e.target;
    caretRef.current = field.selectionStart;
    setInnerText(field.value);
    if (onChange) onChange(e);
    const native = e.nativeEvent;
    if (
      status === "ready" &&
      native &&
      native.inputType === "insertText" &&
      native.data &&
      DELIMITER.test(native.data)
    ) {
      maybeAutocorrect(field);
    }
  };

  const handleSelect = (e) => {
    caretRef.current = e.target.selectionStart;
    if (onSelect) onSelect(e);
  };

  const handleContextMenu = (e) => {
    if (onContextMenu) onContextMenu(e);
    const layer = backdropRef.current;
    if (status !== "ready" || !layer) return;
    const spans = layer.querySelectorAll("[data-spell-start]");
    for (const span of spans) {
      const hitRect = [...span.getClientRects()].find(
        (r) =>
          e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom,
      );
      if (!hitRect) continue;
      e.preventDefault();
      const start = Number(span.dataset.spellStart);
      const end = Number(span.dataset.spellEnd);
      const word = text.slice(start, end);
      setMenu({
        x: e.clientX,
        anchorTop: hitRect.top,
        anchorBottom: hitRect.bottom,
        word,
        start,
        end,
        suggestions: null,
      });
      suggestFor(word)
        .then((suggestions) =>
          setMenu((m) => (m && m.start === start ? { ...m, suggestions } : m)),
        )
        .catch(() =>
          setMenu((m) => (m && m.start === start ? { ...m, suggestions: [] } : m)),
        );
      return;
    }
  };

  const pickSuggestion = (replacement) => {
    const field = getField();
    const target = menu;
    setMenu(null);
    if (field && target) {
      replaceRange(
        field,
        target.start,
        target.end,
        matchCase(target.word, replacement),
      );
    }
  };

  // Underline layer content: plain text with wavy spans on misspelled words,
  // skipping the word still being typed at the caret.
  const segments = [];
  let cursor = 0;
  bad
    .filter(
      (t) =>
        text.slice(t.start, t.end) === t.word &&
        !(t.end === caretRef.current && document.activeElement === getField()),
    )
    .sort((a, b) => a.start - b.start)
    .forEach((t) => {
      if (t.start < cursor) return;
      segments.push(text.slice(cursor, t.start));
      segments.push(
        <span
          key={t.start}
          data-spell-start={t.start}
          data-spell-end={t.end}
          style={{
            textDecoration: "underline wavy #e5484d",
            textDecorationSkipInk: "none",
          }}
        >
          {t.word}
        </span>,
      );
      cursor = t.end;
    });
  segments.push(text.slice(cursor));

  const multiline = as !== "input";

  return (
    <>
      <Component
        {...rest}
        ref={setRefs}
        value={value}
        spellCheck={status !== "ready"}
        onFocus={handleFocus}
        onChange={handleChange}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        onScroll={onScroll}
        onContextMenu={handleContextMenu}
      />
      {status === "ready" && (
        <div
          ref={backdropRef}
          aria-hidden="true"
          style={{
            position: "absolute",
            // antd's affix wrapper (used with showCount) is z-index: 1.
            zIndex: 2,
            pointerEvents: "none",
            overflow: "hidden",
            boxSizing: "border-box",
            color: "transparent",
            whiteSpace: multiline ? "pre-wrap" : "pre",
            overflowWrap: multiline ? "break-word" : "normal",
            borderStyle: "solid",
            borderColor: "transparent",
          }}
        >
          {segments}
          {"​"}
        </div>
      )}
      {menu && (
        <SuggestionMenu
          key={menu.start}
          menu={menu}
          onPick={pickSuggestion}
          onIgnore={() => {
            ignoreWord(menu.word);
            setMenu(null);
          }}
          onAdd={() => {
            addToPersonalDictionary(menu.word);
            setMenu(null);
          }}
          onClose={() => setMenu(null)}
        />
      )}
    </>
  );
});

export default SpellChecked;
